import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppContext } from '../../contexts/ThemeContext';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Typography,
} from '@mui/material';
import {
  FaChevronDown,
  FaChevronUp,
  FaTachometerAlt,
  FaUtensils,
  FaStar,
  FaUsers,
  FaBell,
  FaTicketAlt,
  FaTags,
  FaClock,
  FaInfoCircle,
  FaCalendarAlt,
  FaPepperHot,
  FaPizzaSlice,
  FaGift,
  FaStore,
  FaCalculator,
  FaChartLine,
  FaShoppingCart,
} from 'react-icons/fa';

const DRAWER_WIDTH = 260;

function Sidebar() {
  const location = useLocation();
  const { drawerOpen } = useAppContext();
  const [activeTab, setActiveTab] = useState(location.pathname);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTruckOpen, setIsTruckOpen] = useState(false);

  useEffect(() => {
    setActiveTab(location.pathname);
  }, [location]);

  const handleTabClick = (path) => {
    setActiveTab(path);
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand area */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Administration
        </Typography>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        <List>
          <SidebarItem
            to="/"
            icon={<FaTachometerAlt />}
            text="Tableau de bord"
            activeTab={activeTab}
            handleTabClick={handleTabClick}
          />

          <Divider sx={{ my: 1 }} />

          {/* Menu Management Section */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              sx={{
                borderRadius: 1,
                mx: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FaUtensils size={18} />
              </ListItemIcon>
              <ListItemText primary="Menu" />
              {isMenuOpen ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
            </ListItemButton>
          </ListItem>
          <Collapse in={isMenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 1 }}>
              <SidebarSubItem
                to="/menu/categories"
                icon={<FaTags />}
                text="Catégories"
                activeTab={activeTab}
                handleTabClick={handleTabClick}
              />
              <SidebarSubItem
                to="/menu/products"
                icon={<FaPizzaSlice />}
                text="Produits"
                activeTab={activeTab}
                handleTabClick={handleTabClick}
              />
              <SidebarSubItem
                to="/menu/ingredients"
                icon={<FaPepperHot />}
                text="Ingrédients"
                activeTab={activeTab}
                handleTabClick={handleTabClick}
              />
              <SidebarSubItem
                to="/menu/bundles"
                icon={<FaGift />}
                text="Formules"
                activeTab={activeTab}
                handleTabClick={handleTabClick}
              />
            </List>
          </Collapse>

          {/* Truck Management Section */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setIsTruckOpen(!isTruckOpen)}
              sx={{
                borderRadius: 1,
                mx: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FaStore size={18} />
              </ListItemIcon>
              <ListItemText primary="Restaurant" />
              {isTruckOpen ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
            </ListItemButton>
          </ListItem>
          <Collapse in={isTruckOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 1 }}>
              <SidebarSubItem
                to="/truck/info"
                icon={<FaInfoCircle />}
                text="Informations"
                activeTab={activeTab}
                handleTabClick={handleTabClick}
              />
              <SidebarSubItem
                to="/truck/hours"
                icon={<FaClock />}
                text="Horaires"
                activeTab={activeTab}
                handleTabClick={handleTabClick}
              />
              <SidebarSubItem
                to="/truck/slots"
                icon={<FaCalendarAlt />}
                text="Créneaux"
                activeTab={activeTab}
                handleTabClick={handleTabClick}
              />
            </List>
          </Collapse>

          <Divider sx={{ my: 1 }} />

          <SidebarItem
            to="/orders"
            icon={<FaShoppingCart />}
            text="Commandes"
            activeTab={activeTab}
            handleTabClick={handleTabClick}
          />
          <SidebarItem
            to="/customers"
            icon={<FaUsers />}
            text="Clients"
            activeTab={activeTab}
            handleTabClick={handleTabClick}
          />
          <SidebarItem
            to="/promo-codes"
            icon={<FaTicketAlt />}
            text="Codes Promo"
            activeTab={activeTab}
            handleTabClick={handleTabClick}
          />
          <SidebarItem
            to="/loyalty"
            icon={<FaStar />}
            text="Fidélité"
            activeTab={activeTab}
            handleTabClick={handleTabClick}
          />

          <Divider sx={{ my: 1 }} />

          <SidebarItem
            to="/notifications"
            icon={<FaBell />}
            text="Notifications"
            activeTab={activeTab}
            handleTabClick={handleTabClick}
          />
          <SidebarItem
            to="/sales"
            icon={<FaChartLine />}
            text="Ventes"
            activeTab={activeTab}
            handleTabClick={handleTabClick}
          />
          <SidebarItem
            to="/accounting"
            icon={<FaCalculator />}
            text="Comptabilité"
            activeTab={activeTab}
            handleTabClick={handleTabClick}
          />
        </List>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={drawerOpen}
      sx={{
        width: drawerOpen ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          top: 64, // Height of AppBar
          height: 'calc(100vh - 64px)',
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

function SidebarItem({ to, icon, text, activeTab, handleTabClick }) {
  const isActive = activeTab === to;
  
  return (
    <ListItem disablePadding>
      <ListItemButton
        component={Link}
        to={to}
        onClick={() => handleTabClick(to)}
        sx={{
          borderRadius: 1,
          mx: 1,
          bgcolor: isActive ? 'primary.main' : 'transparent',
          color: isActive ? 'primary.contrastText' : 'text.primary',
          '&:hover': {
            bgcolor: isActive ? 'primary.dark' : 'action.hover',
          }
        }}
      >
        <ListItemIcon sx={{ 
          color: 'inherit', 
          minWidth: 40 
        }}>
          {React.cloneElement(icon, { size: 18 })}
        </ListItemIcon>
        <ListItemText 
          primary={text}
          primaryTypographyProps={{ 
            fontSize: '0.875rem',
            fontWeight: isActive ? 500 : 400
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

function SidebarSubItem({ to, icon, text, activeTab, handleTabClick }) {
  const isActive = activeTab === to;
  
  return (
    <ListItem disablePadding>
      <ListItemButton
        component={Link}
        to={to}
        onClick={() => handleTabClick(to)}
        sx={{
          borderRadius: 1,
          mx: 1,
          ml: 2,
          bgcolor: isActive ? 'primary.main' : 'transparent',
          color: isActive ? 'primary.contrastText' : 'text.secondary',
          '&:hover': {
            bgcolor: isActive ? 'primary.dark' : 'action.hover',
          }
        }}
      >
        <ListItemIcon sx={{ 
          color: 'inherit', 
          minWidth: 36 
        }}>
          {React.cloneElement(icon, { size: 16 })}
        </ListItemIcon>
        <ListItemText 
          primary={text}
          primaryTypographyProps={{ 
            fontSize: '0.8125rem',
            fontWeight: isActive ? 500 : 400
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

export default Sidebar;